/**
 * Countries Configuration
 *
 * This file contains the mapping of countries to their dialing codes and SSM CountryIds.
 *
 * IMPORTANT: The CountryId values are placeholders and need to be verified against
 * the SSM API documentation. Update these values as you discover the correct mappings
 * through testing with the SSM API.
 *
 * Initial assumptions:
 * - US CountryId = 1 (confirmed from existing code)
 * - Other countries: Using common conventions, but MUST be tested
 */

export interface Country {
    code: string;           // ISO country code (e.g., "US", "CA")
    name: string;           // Full country name
    dialingCode: string;    // International dialing code (e.g., "+1")
    countryId: number;      // SSM API CountryId (MUST be verified with SSM)
}

/**
 * List of all countries with their dialing codes and SSM CountryIds
 *
 * NOTE: CountryId values are placeholder assumptions and MUST be verified
 * with SSM API documentation or through testing. Update as you discover
 * the correct mappings.
 */
export const COUNTRIES: Country[] = [
    // North America
    { code: "US", name: "United States", dialingCode: "+1", countryId: 1 },
    { code: "CA", name: "Canada", dialingCode: "+1", countryId: 2 },

    // Europe
    { code: "GB", name: "United Kingdom", dialingCode: "+44", countryId: 44 },
    { code: "DE", name: "Germany", dialingCode: "+49", countryId: 49 },
    { code: "FR", name: "France", dialingCode: "+33", countryId: 33 },
    { code: "ES", name: "Spain", dialingCode: "+34", countryId: 34 },
    { code: "IT", name: "Italy", dialingCode: "+39", countryId: 39 },
    { code: "NL", name: "Netherlands", dialingCode: "+31", countryId: 31 },
    { code: "BE", name: "Belgium", dialingCode: "+32", countryId: 32 },
    { code: "CH", name: "Switzerland", dialingCode: "+41", countryId: 41 },
    { code: "AT", name: "Austria", dialingCode: "+43", countryId: 43 },
    { code: "SE", name: "Sweden", dialingCode: "+46", countryId: 46 },
    { code: "NO", name: "Norway", dialingCode: "+47", countryId: 47 },
    { code: "DK", name: "Denmark", dialingCode: "+45", countryId: 45 },
    { code: "FI", name: "Finland", dialingCode: "+358", countryId: 358 },
    { code: "IE", name: "Ireland", dialingCode: "+353", countryId: 353 },
    { code: "PL", name: "Poland", dialingCode: "+48", countryId: 48 },
    { code: "PT", name: "Portugal", dialingCode: "+351", countryId: 351 },
    { code: "GR", name: "Greece", dialingCode: "+30", countryId: 30 },

    // Asia
    { code: "CN", name: "China", dialingCode: "+86", countryId: 86 },
    { code: "JP", name: "Japan", dialingCode: "+81", countryId: 81 },
    { code: "IN", name: "India", dialingCode: "+91", countryId: 91 },
    { code: "KR", name: "South Korea", dialingCode: "+82", countryId: 82 },
    { code: "SG", name: "Singapore", dialingCode: "+65", countryId: 65 },
    { code: "MY", name: "Malaysia", dialingCode: "+60", countryId: 60 },
    { code: "TH", name: "Thailand", dialingCode: "+66", countryId: 66 },
    { code: "PH", name: "Philippines", dialingCode: "+63", countryId: 63 },
    { code: "ID", name: "Indonesia", dialingCode: "+62", countryId: 62 },
    { code: "VN", name: "Vietnam", dialingCode: "+84", countryId: 84 },
    { code: "HK", name: "Hong Kong", dialingCode: "+852", countryId: 852 },
    { code: "TW", name: "Taiwan", dialingCode: "+886", countryId: 886 },

    // Middle East
    { code: "AE", name: "United Arab Emirates", dialingCode: "+971", countryId: 971 },
    { code: "SA", name: "Saudi Arabia", dialingCode: "+966", countryId: 966 },
    { code: "IL", name: "Israel", dialingCode: "+972", countryId: 972 },
    { code: "TR", name: "Turkey", dialingCode: "+90", countryId: 90 },

    // Oceania
    { code: "AU", name: "Australia", dialingCode: "+61", countryId: 61 },
    { code: "NZ", name: "New Zealand", dialingCode: "+64", countryId: 64 },

    // South America
    { code: "BR", name: "Brazil", dialingCode: "+55", countryId: 55 },
    { code: "MX", name: "Mexico", dialingCode: "+52", countryId: 52 },
    { code: "AR", name: "Argentina", dialingCode: "+54", countryId: 54 },
    { code: "CL", name: "Chile", dialingCode: "+56", countryId: 56 },
    { code: "CO", name: "Colombia", dialingCode: "+57", countryId: 57 },
    { code: "PE", name: "Peru", dialingCode: "+51", countryId: 51 },

    // Africa
    { code: "ZA", name: "South Africa", dialingCode: "+27", countryId: 27 },
    { code: "EG", name: "Egypt", dialingCode: "+20", countryId: 20 },
    { code: "NG", name: "Nigeria", dialingCode: "+234", countryId: 234 },
    { code: "KE", name: "Kenya", dialingCode: "+254", countryId: 254 },

    // Additional European countries
    { code: "RU", name: "Russia", dialingCode: "+7", countryId: 7 },
    { code: "CZ", name: "Czech Republic", dialingCode: "+420", countryId: 420 },
    { code: "HU", name: "Hungary", dialingCode: "+36", countryId: 36 },
    { code: "RO", name: "Romania", dialingCode: "+40", countryId: 40 },

    // Additional countries
    { code: "PK", name: "Pakistan", dialingCode: "+92", countryId: 92 },
    { code: "BD", name: "Bangladesh", dialingCode: "+880", countryId: 880 },
    { code: "LK", name: "Sri Lanka", dialingCode: "+94", countryId: 94 },
];

/**
 * Default country (United States)
 */
export const DEFAULT_COUNTRY = COUNTRIES[0]; // US
